/**
 * BrokenImageBadge — red "broken" marker overlaid on a failed image's fallback.
 *
 * @layer-1 — pair with `useBrokenImage` (or Radix Avatar error status) wherever an
 * image can 404 / be corrupt: title covers, profile photos, publisher photos.
 *
 * Variants:
 *   - default → red text bar pinned to the bottom edge (covers, large photos)
 *   - compact → small red corner dot (tiny avatars where text won't fit)
 *
 * Parent must be `position: relative`.
 */
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/utils";

interface BrokenImageBadgeProps {
  /** Use the corner-dot form for small avatars. Default: text bar. */
  compact?: boolean;
  className?: string;
}

export function BrokenImageBadge({ compact = false, className }: BrokenImageBadgeProps) {
  const { t } = useTranslation();
  const label = t("common.imageBroken");

  if (compact) {
    return (
      <span
        title={label}
        aria-label={label}
        className={cn(
          "absolute top-0 end-0 block size-2 rounded-full ring-1 ring-[var(--card)]",
          className,
        )}
        style={{ background: "var(--color-error)" }}
      />
    );
  }

  return (
    <span
      className={cn(
        "absolute inset-x-0 bottom-0 truncate px-1 py-0.5 text-center font-semibold",
        className,
      )}
      style={{
        fontSize: "9px",
        lineHeight: 1.25,
        color: "var(--color-error)",
        background: "var(--color-error-bg)",
      }}
    >
      {label}
    </span>
  );
}
